import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CodeNameModel } from 'components/common/types';
import {
  ActivatedNonMediaService,
  NonMediaService,
  NonMediaServiceStatus,
  TotalServiceCpm,
} from 'components/common/types/NonMediaServices.types';

export interface NonMediaServicesStore {
  selectedNonMediaServices: ActivatedNonMediaService[];
  availableNonMediaServices: NonMediaService[];
  filteredNonMediaServices: NonMediaService[];
  categoryFilters: CodeNameModel[];
  areServicesChanged: boolean;
  servicesSearchQuery?: string;
}

const initialState: NonMediaServicesStore = {
  selectedNonMediaServices: [],
  availableNonMediaServices: [],
  filteredNonMediaServices: [],
  categoryFilters: [],
  areServicesChanged: false,
  servicesSearchQuery: '',
};

const unselectServiceById = (services: NonMediaService[], serviceId: string): NonMediaService[] =>
  services.map((service) => (service.id === serviceId ? { ...service, isSelected: false } : service));

export const nonMediaServicesSlice = createSlice({
  name: 'nonMediaServices',
  initialState,
  reducers: {
    updateAvailableNonMediaServices: (
      state,
      action: PayloadAction<{ nonMediaServices: NonMediaService[]; lineId?: string }>,
    ) => {
      const selectedIds = new Set(
        state.selectedNonMediaServices.flatMap((service) => {
          if (!!action.payload.lineId && service.dealId !== action.payload.lineId) return [];
          return [service.measurement.id];
        }),
      );

      state.availableNonMediaServices = action.payload.nonMediaServices.map((service) => ({
        ...service,
        isSelected: selectedIds.has(service.id),
      }));
    },
    updateSelectedNonMediaServices: (
      state,
      action: PayloadAction<{ selectedNonMediaServices: ActivatedNonMediaService[]; lineId?: string }>,
    ) => {
      state.selectedNonMediaServices = action.payload.selectedNonMediaServices;

      const selectedIds = new Set(
        action.payload.selectedNonMediaServices.flatMap((service) => {
          if (!!action.payload.lineId && service.dealId !== action.payload.lineId) return [];
          return [service.measurement.id];
        }),
      );

      state.availableNonMediaServices = state.availableNonMediaServices.map((service) => ({
        ...service,
        isSelected: selectedIds.has(service.id),
      }));

      state.filteredNonMediaServices = state.filteredNonMediaServices.map((service) => ({
        ...service,
        isSelected: selectedIds.has(service.id),
      }));
    },
    appendNewService: (
      state,
      action: PayloadAction<{
        service: NonMediaService;
        contactEmails: string[];
        currentLineId: string;
        totalServiceCpm: TotalServiceCpm;
      }>,
    ) => {
      const { service, contactEmails, currentLineId, totalServiceCpm } = action.payload;

      const { value: totalCpm, currency: totalCpmCurrency } = totalServiceCpm;

      const newService: ActivatedNonMediaService = {
        id: service.id,
        status: NonMediaServiceStatus.SELECTED,
        measurement: service,
        contactEmails,
        eligibilityRuleId: service.eligibilityRules[0].id,
        pricingConfigId: service.eligibilityRules[0].pricingConfigs[0].id,
        dealId: currentLineId,
        totalCpm,
        totalCpmCurrency,
      };

      state.areServicesChanged = true;

      state.selectedNonMediaServices = [...state.selectedNonMediaServices, newService];

      state.availableNonMediaServices = state.availableNonMediaServices.map((availableService) => ({
        ...availableService,
        isSelected: availableService.id === service.id ? true : availableService.isSelected,
      }));

      state.filteredNonMediaServices = state.filteredNonMediaServices.map((availableService) => ({
        ...availableService,
        isSelected: availableService.id === service.id ? true : availableService.isSelected,
      }));
    },
    cancelExistingService: (state, action: PayloadAction<{ serviceId: string }>) => {
      const selectedService = state.selectedNonMediaServices.find((serv) => serv.id === action.payload.serviceId);
      if (!selectedService) return;

      const { id } = selectedService;

      if (selectedService.status === NonMediaServiceStatus.SELECTED) {
        state.areServicesChanged = state.selectedNonMediaServices.length > 1 && state.areServicesChanged;
        state.selectedNonMediaServices = state.selectedNonMediaServices.filter((service) => service.id !== id);
      } else {
        state.areServicesChanged = true;
        selectedService.status = NonMediaServiceStatus.REQUESTED_CANCELLATION;
      }

      state.availableNonMediaServices = unselectServiceById(state.availableNonMediaServices, id);
      state.filteredNonMediaServices = unselectServiceById(state.filteredNonMediaServices, id);
    },
    resetServicesChanged: (state) => {
      state.areServicesChanged = false;
    },
    updateCategoryFilters: (state, action: PayloadAction<CodeNameModel[]>) => {
      state.categoryFilters = action.payload;
    },
    resetCategoryFilters: (state) => {
      state.categoryFilters = [];
    },
    updateServicesSearchQuery: (state, action: PayloadAction<string>) => {
      state.servicesSearchQuery = action.payload;
    },
    resetServicesSearchQuery: (state) => {
      state.servicesSearchQuery = '';
    },
    updateFilteredNonMediaServices: (
      state,
      action: PayloadAction<{ nonMediaServices: NonMediaService[]; lineId?: string }>,
    ) => {
      const selectedIds = new Set(
        state.selectedNonMediaServices.flatMap((service) => {
          if (!!action.payload.lineId && service.dealId !== action.payload.lineId) return [];
          return [service.measurement.id];
        }),
      );

      state.filteredNonMediaServices = action.payload.nonMediaServices.map((service) => ({
        ...service,
        isSelected: selectedIds.has(service.id),
      }));
    },
  },
});

export const {
  updateAvailableNonMediaServices,
  updateSelectedNonMediaServices,
  appendNewService,
  cancelExistingService,
  resetServicesChanged,
  updateFilteredNonMediaServices,
  updateCategoryFilters,
  resetCategoryFilters,
  updateServicesSearchQuery,
  resetServicesSearchQuery,
} = nonMediaServicesSlice.actions;

export default nonMediaServicesSlice.reducer;
